import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import type { Notice } from '@/shared/notices/types';
import {
  useMarkNoticesSeen,
  useNoticesAllowed,
  usePendingNotices,
} from '@/shared/notices/useNotices';
import { Button } from '@/shared/ui/Button';
import { Dialog } from '@/shared/ui/Dialog';

import { NoticeItem } from './NoticeItem';

/**
 * How long after a change of screen its answer may still open the window. Later (a phone
 * that lost the network in a basement, a retry after a 502) the person is already doing
 * something on that screen: it waits for the next one.
 */
export const NOTICE_ARM_MS = 3_000;

/** Another dialog is open (the bell, the password of the elevated mode…): not on top. */
function anotherDialogOpen(): boolean {
  return document.querySelector('[aria-modal="true"]') !== null;
}

/**
 * The window of the notices to the team (contract v10.50, ADR 0045 of the back): what this
 * person has not seen yet, newest first, until they say «Entendido». Closing it with «×»,
 * Escape or a click outside counts too, as was said on 24/09.
 *
 * It opens on coming in and on moving to another screen, never in the middle of one: a
 * notice that arrives while somebody writes a quote waits for their next change of screen,
 * instead of taking the focus from the form. A change of screen asks again what is pending,
 * and only an answer that came after it, and soon (`NOTICE_ARM_MS`), may open the window:
 * what the last poll brought may be a minute old, and an answer that took long finds the
 * person busy. Not over another dialog either. What was marked seen here, in the window or
 * in the bell, does not come back while the answer of «Entendido» is on its way
 * (`useMarkNoticesSeen`).
 */
export function NoticeWindow() {
  const location = useLocation();
  const allowed = useNoticesAllowed();
  const pending = usePendingNotices();
  const { refetch, data, dataUpdatedAt, errorUpdatedAt } = pending;
  const seen = useMarkNoticesSeen();
  const [shown, setShown] = useState<Notice[] | null>(null);
  const closed = useRef(new Set<string>());
  // When it was armed (coming in, each change of screen); null once spent.
  const armedAt = useRef<number | null>(null);

  useEffect(() => {
    if (!allowed) return;
    armedAt.current = Date.now();
    void refetch({ cancelRefetch: false });
  }, [allowed, location.pathname, refetch]);

  useEffect(() => {
    const armed = armedAt.current;
    if (shown || armed === null) return;
    if (errorUpdatedAt >= armed) {
      // Nothing could be asked: no window until the next screen, rather than mid-screen.
      armedAt.current = null;
      return;
    }
    if (!data || dataUpdatedAt < armed) return;
    armedAt.current = null;
    if (dataUpdatedAt - armed > NOTICE_ARM_MS || anotherDialogOpen()) return;
    const fresh = data.items.filter((notice) => !closed.current.has(notice.id));
    if (fresh.length > 0) setShown(fresh);
  }, [data, dataUpdatedAt, errorUpdatedAt, location.pathname, shown]);

  if (!shown) return null;

  function close() {
    if (!shown) return;
    const ids = shown.map((notice) => notice.id);
    for (const id of ids) closed.current.add(id);
    seen.mutate(ids);
    setShown(null);
  }

  const [only] = shown;
  return (
    <Dialog
      title={shown.length === 1 && only ? 'Aviso' : `${shown.length} avisos`}
      description="Lo que hay que saber desde la última vez. Después sigue en la campana."
      onClose={close}
      footer={
        <Button data-autofocus onClick={close}>
          Entendido
        </Button>
      }
    >
      <div className="divide-line-2 flex flex-col divide-y">
        {shown.map((notice) => (
          <div key={notice.id} className="py-3 first:pt-0 last:pb-0">
            <NoticeItem notice={notice} onFollow={close} />
          </div>
        ))}
      </div>
    </Dialog>
  );
}
